// src/components/settings/BusinessInfoTab.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, Edit2, X, Save, Building2, MapPin, Phone, Mail, User, MessageSquare, Smartphone, Bell, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { useBusinessContext } from '../../contexts/BusinessContext';

const BusinessInfoTab = ({ user, businessName: initialBusinessName }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Workflow mode (business-level view preference)
  const { isOwner, workflowMode, updateUserFields } = useBusinessContext();
  const [modeSaving, setModeSaving] = useState(false);
  const [modeError, setModeError] = useState(null);

  const handleWorkflowModeChange = async (mode) => {
    if (mode === workflowMode || modeSaving) return;
    setModeSaving(true);
    setModeError(null);
    try {
      await api.put('/business/workflow-mode', { workflowMode: mode });
      updateUserFields({ workflowMode: mode });
    } catch (err) {
      setModeError(err.response?.data?.error || 'Failed to change mode');
    } finally {
      setModeSaving(false);
    }
  };
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  // SMS settings state (separate from main form)
  const [smsSettings, setSmsSettings] = useState({
    ownerMobilePhone: '',
    smsNotificationsEnabled: false,
    smsOnQuoteAccepted: false,
    smsOnNewLead: false,
    smsQuoteTextingEnabled: false
  });
  const [smsEditing, setSmsEditing] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const [smsSuccess, setSmsSuccess] = useState(false);
  const [originalSmsSettings, setOriginalSmsSettings] = useState(null);

  // Original data for cancel
  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    loadBusinessProfile();
  }, []);

  const loadBusinessProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get('/business/profile');
      const profile = response.data;
      const data = {
        name: profile.name || '',
        ownerName: profile.ownerName || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zipCode: profile.zipCode || ''
      };
      setFormData(data);
      setOriginalData(data);

      // Load SMS settings
      const smsData = {
        ownerMobilePhone: profile.ownerMobilePhone || '',
        smsNotificationsEnabled: profile.smsNotificationsEnabled || false,
        smsOnQuoteAccepted: profile.smsOnQuoteAccepted || false,
        smsOnNewLead: profile.smsOnNewLead || false,
        smsQuoteTextingEnabled: profile.smsQuoteTextingEnabled || false
      };
      setSmsSettings(smsData);
      setOriginalSmsSettings(smsData);
    } catch (err) {
      console.error('Error loading profile:', err);
      // Fall back to user context data
      const data = {
        name: initialBusinessName || '',
        ownerName: user?.ownerName || '',
        email: user?.email || '',
        phone: user?.phone || '',
        address: user?.address || '',
        city: user?.city || '',
        state: user?.state || '',
        zipCode: user?.zipCode || ''
      };
      setFormData(data);
      setOriginalData(data);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
    setSuccess(false);
  };

  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.name.trim()) {
      setError('Business name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.put('/business/profile', formData);
      setOriginalData(formData);
      setIsEditing(false);
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // SMS Settings handlers
  const handleSmsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSmsSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setSmsError(null);
    setSmsSuccess(false);
  };

  const handleSmsMobileChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setSmsSettings(prev => ({ ...prev, ownerMobilePhone: formatted }));
  };

  const handleSmsCancel = () => {
    setSmsSettings(originalSmsSettings);
    setSmsEditing(false);
    setSmsError(null);
  };

  const handleSmsSave = async () => {
    // Validate phone if SMS is enabled
    if (smsSettings.smsNotificationsEnabled && !smsSettings.ownerMobilePhone.trim()) {
      setSmsError('Mobile phone number is required when SMS notifications are enabled');
      return;
    }

    // Validate phone format (should have 10 digits)
    if (smsSettings.ownerMobilePhone) {
      const digits = smsSettings.ownerMobilePhone.replace(/\D/g, '');
      if (digits.length !== 10) {
        setSmsError('Please enter a valid 10-digit phone number');
        return;
      }
    }

    setSmsSaving(true);
    setSmsError(null);

    try {
      await api.put('/business/sms-settings', smsSettings);
      setOriginalSmsSettings(smsSettings);
      setSmsEditing(false);
      setSmsSuccess(true);
      
      setTimeout(() => setSmsSuccess(false), 3000);
    } catch (err) {
      setSmsError(err.response?.data?.error || 'Failed to save SMS settings');
    } finally {
      setSmsSaving(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Workflow Mode Card */}
      {isOwner && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">How You Work</h2>
          <p className="text-sm text-gray-600 mb-4">
            Choose how much of SuddyFlow you see. You can switch anytime and nothing is deleted.
          </p>

          {modeError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {modeError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              {
                value: 'Simple',
                title: 'Simple',
                desc: 'Customers, jobs, invoices and tax tracking. Best if you agree on price and date up front and just need professional invoices.',
              },
              {
                value: 'Standard',
                title: 'Standard',
                desc: 'Everything: quotes, scheduling, leads, agreements, team and more.',
              },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={modeSaving}
                onClick={() => handleWorkflowModeChange(opt.value)}
                className={`text-left p-4 rounded-lg border-2 transition-colors disabled:opacity-60 ${
                  workflowMode === opt.value
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">{opt.title}</span>
                  {workflowMode === opt.value && (
                    <CheckCircle size={18} className="text-primary-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Industry Information Card */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Industry Information</h2>
        <p className="text-sm text-gray-600 mb-6">
          Your industry selection determines your app's theme, terminology, and default services.
        </p>
        
        <div className="space-y-4">
          <div className="p-4 bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Service Type
            </label>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {user?.primaryServiceType?.replace(/([A-Z])/g, ' $1').trim() || 'Not Set'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  This drives your app's theme, colors, and terminology
                </p>
              </div>
              <div className="px-3 py-1 bg-white rounded-lg border border-primary-300">
                <span className="text-xs font-medium text-primary-700 uppercase tracking-wide">Primary</span>
              </div>
            </div>
          </div>

          {user?.additionalServiceTypes && user.additionalServiceTypes.length > 0 && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Additional Services
              </label>
              <div className="flex flex-wrap gap-2">
                {user.additionalServiceTypes.map((service) => (
                  <span key={service} className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700">
                    {service.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                ))}
              </div>
            </div>
          )}

          {user?.serviceTypesSeeded && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {user.serviceTypesSeeded} Service Types Pre-Configured
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Based on your industry selection. Manage them in the Service Types tab.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Business Information Card - EDITABLE */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Business Information</h2>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Edit2 size={16} />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Save size={16} />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">Business information updated successfully!</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Business Name */}
          <div>
            <label className="label flex items-center gap-2">
              <Building2 size={14} className="text-gray-400" />
              Business Name *
            </label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="input"
                placeholder="Your Business Name"
              />
            ) : (
              <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.name || '-'}</p>
            )}
          </div>

          {/* Owner Name */}
          <div>
            <label className="label flex items-center gap-2">
              <User size={14} className="text-gray-400" />
              Owner Name
            </label>
            {isEditing ? (
              <input
                type="text"
                name="ownerName"
                value={formData.ownerName}
                onChange={handleChange}
                className="input"
                placeholder="John Smith"
              />
            ) : (
              <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.ownerName || '-'}</p>
            )}
          </div>

          {/* Email & Phone Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label flex items-center gap-2">
                <Mail size={14} className="text-gray-400" />
                Business Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input"
                  placeholder="contact@yourbusiness.com"
                />
              ) : (
                <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.email || '-'}</p>
              )}
            </div>
            <div>
              <label className="label flex items-center gap-2">
                <Phone size={14} className="text-gray-400" />
                Business Phone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="input"
                  placeholder="(555) 123-4567"
                  maxLength={14}
                />
              ) : (
                <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.phone || '-'}</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="label flex items-center gap-2">
              <MapPin size={14} className="text-gray-400" />
              Street Address
            </label>
            {isEditing ? (
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="input"
                placeholder="123 Main Street"
              />
            ) : (
              <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.address || '-'}</p>
            )}
          </div>

          {/* City, State, Zip Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 md:col-span-2">
              <label className="label">City</label>
              {isEditing ? (
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="input"
                  placeholder="Mooresville"
                />
              ) : (
                <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.city || '-'}</p>
              )}
            </div>
            <div>
              <label className="label">State</label>
              {isEditing ? (
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="">Select</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              ) : (
                <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.state || '-'}</p>
              )}
            </div>
            <div>
              <label className="label">ZIP Code</label>
              {isEditing ? (
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="input"
                  placeholder="28117"
                  maxLength={10}
                />
              ) : (
                <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">{formData.zipCode || '-'}</p>
              )}
            </div>
          </div>

          {/* Location Coordinates - Read Only with Refresh */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="label">📍 Location Coordinates</label>
              {!isEditing && (
                <button
                  onClick={async () => {
                    try {
                      const response = await api.post('/business/geocode-address');
                      setFormData(prev => ({
                        ...prev,
                        latitude: response.data.latitude,
                        longitude: response.data.longitude
                      }));
                      setSuccess(true);
                    } catch (err) {
                      setError('Could not geocode address');
                    }
                  }}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  🔄 Refresh Location
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input 
                  type="text" 
                  value={formData.latitude || 'Not set'} 
                  disabled 
                  className="input bg-gray-50 text-xs font-mono" 
                />
              </div>
              <div>
                <input 
                  type="text" 
                  value={formData.longitude || 'Not set'} 
                  disabled 
                  className="input bg-gray-50 text-xs font-mono" 
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Used for weather forecasts and route optimization. Updates automatically when you change your address.
            </p>
          </div>

          {/* Business ID - Always Read Only */}
          <div className="pt-4 border-t border-gray-200">
            <label className="label">Business ID</label>
            <input 
              type="text" 
              value={user?.businessId || ''} 
              disabled 
              className="input bg-gray-50 font-mono text-sm" 
            />
            <p className="text-xs text-gray-500 mt-1">Use this ID when contacting support</p>
          </div>
        </div>
      </div>

      {/* SMS Notifications Card - NEW */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MessageSquare size={20} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">SMS Notifications</h2>
              <p className="text-sm text-gray-500">Get instant text alerts for important events</p>
            </div>
          </div>
          {!smsEditing ? (
            <button
              onClick={() => setSmsEditing(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Edit2 size={16} />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSmsCancel}
                disabled={smsSaving}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSmsSave}
                disabled={smsSaving}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Save size={16} />
                {smsSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* SMS Success Message */}
        {smsSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle size={18} />
            <span className="text-sm font-medium">SMS settings updated successfully!</span>
          </div>
        )}

        {/* SMS Error Message */}
        {smsError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle size={18} />
            <span className="text-sm">{smsError}</span>
          </div>
        )}

        <div className="space-y-6">
          {/* Mobile Phone Number */}
          <div>
            <label className="label flex items-center gap-2">
              <Smartphone size={14} className="text-gray-400" />
              Owner Mobile Phone
            </label>
            {smsEditing ? (
              <>
                <input
                  type="tel"
                  name="ownerMobilePhone"
                  value={smsSettings.ownerMobilePhone}
                  onChange={handleSmsMobileChange}
                  className="input"
                  placeholder="(555) 123-4567"
                  maxLength={14}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is where you'll receive SMS notifications
                </p>
              </>
            ) : (
              <p className="py-2 px-3 bg-gray-50 rounded-lg text-gray-900">
                {smsSettings.ownerMobilePhone || <span className="text-gray-400 italic">Not configured</span>}
              </p>
            )}
          </div>

          {/* Master Toggle */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">Enable SMS Notifications</p>
                  <p className="text-sm text-gray-500">Master toggle for all text message alerts</p>
                </div>
              </div>
              {smsEditing ? (
                <div className="relative">
                  <input
                    type="checkbox"
                    name="smsNotificationsEnabled"
                    checked={smsSettings.smsNotificationsEnabled}
                    onChange={handleSmsChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 cursor-pointer"></div>
                </div>
              ) : (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  smsSettings.smsNotificationsEnabled 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {smsSettings.smsNotificationsEnabled ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </label>
          </div>

          {/* Notification Types */}
<div className={`space-y-3 ${!smsSettings.smsNotificationsEnabled && !smsEditing ? 'opacity-50' : ''}`}>
  <p className="text-sm font-medium text-gray-700">Notify me when:</p>
  
  {/* Quote Accepted */}
  <label className={`flex items-center justify-between p-3 rounded-lg border ${
    smsSettings.smsOnQuoteAccepted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
  } ${smsEditing && smsSettings.smsNotificationsEnabled ? 'cursor-pointer hover:border-primary-300' : ''}`}>
    <div className="flex items-center gap-3">
      <span className="text-xl">🎉</span>
      <div>
        <p className="font-medium text-gray-900">Quote Accepted</p>
        <p className="text-sm text-gray-500">Customer accepts a quote via the online quote link</p>
      </div>
    </div>
    {smsEditing ? (
      <input
        type="checkbox"
        name="smsOnQuoteAccepted"
        checked={smsSettings.smsOnQuoteAccepted}
        onChange={handleSmsChange}
        disabled={!smsSettings.smsNotificationsEnabled}
        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
      />
    ) : (
      smsSettings.smsOnQuoteAccepted && (
        <CheckCircle size={20} className="text-green-600" />
      )
    )}
  </label>

  {/* New Lead */}
  <label className={`flex items-center justify-between p-3 rounded-lg border ${
    smsSettings.smsOnNewLead ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
  } ${smsEditing && smsSettings.smsNotificationsEnabled ? 'cursor-pointer hover:border-primary-300' : ''}`}>
    <div className="flex items-center gap-3">
      <span className="text-xl">📥</span>
      <div>
        <p className="font-medium text-gray-900">New Lead</p>
        <p className="text-sm text-gray-500">Someone submits your public quote request form</p>
      </div>
    </div>
    {smsEditing ? (
      <input
        type="checkbox"
        name="smsOnNewLead"
        checked={smsSettings.smsOnNewLead}
        onChange={handleSmsChange}
        disabled={!smsSettings.smsNotificationsEnabled}
        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
      />
    ) : (
      smsSettings.smsOnNewLead && (
        <CheckCircle size={20} className="text-green-600" />
      )
    )}
  </label>

  {/* Quote Texting Feature */}
  <label className={`flex items-center justify-between p-3 rounded-lg border ${
    smsSettings.smsQuoteTextingEnabled ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
  } ${smsEditing && smsSettings.smsNotificationsEnabled ? 'cursor-pointer hover:border-primary-300' : ''}`}>
    <div className="flex items-center gap-3">
      <span className="text-xl">📲</span>
      <div>
        <p className="font-medium text-gray-900">Text Quotes &amp; Invoices to Customers</p>
        <p className="text-sm text-gray-500">Enables texting quote and invoice links directly to a customer's phone</p>
      </div>
    </div>
    {smsEditing ? (
      <input
        type="checkbox"
        name="smsQuoteTextingEnabled"
        checked={smsSettings.smsQuoteTextingEnabled}
        onChange={handleSmsChange}
        disabled={!smsSettings.smsNotificationsEnabled}
        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
      />
    ) : (
      smsSettings.smsQuoteTextingEnabled && (
        <CheckCircle size={20} className="text-green-600" />
      )
    )}
  </label>
</div>

          {/* Info Note */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Standard message rates may apply from your carrier. 
              SMS notifications are sent to the owner's mobile phone only.
            </p>
          </div>
        </div>
      </div>

      {/* Account Details Card */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Details</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Account Created</span>
            <span className="text-sm font-medium text-gray-900">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Subscription Status</span>
            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
              user?.subscriptionStatus === 'Active' ? 'bg-green-100 text-green-800'
              : user?.subscriptionStatus === 'Trial' ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-100 text-gray-800'
            }`}>
              {user?.subscriptionStatus || 'N/A'}
            </span>
          </div>
          {user?.subscriptionPlan && (
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Current Plan</span>
              <span className="text-sm font-medium text-gray-900">{user.subscriptionPlan}</span>
            </div>
          )}
          {user?.subscriptionStatus === 'Trial' && user?.trialDaysRemaining && (
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">Trial Days Remaining</span>
              <span className="text-sm font-semibold text-blue-600">{user.trialDaysRemaining} days</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessInfoTab;